// Generación client-side del PDF del reporte de entregabilidad de email.
//
// @react-pdf/renderer arma un PDF vectorial 100% en el cliente (texto
// seleccionable, tipografía Inter embebida — la misma del backoffice) y acá
// se dispara la descarga de un archivo .pdf real, sin window.print (que en
// mobile no es confiable).
//
// El documento vive en ./pdf/EmailReportPdfDoc.tsx. Todo se carga con
// import() dinámico: react-pdf + fuentes solo bajan cuando alguien aprieta
// "Bajar como PDF".

import { createElement, type ReactElement } from 'react';
import type { DocumentProps } from '@react-pdf/renderer';
import type { EmailReport } from '@/lib/api';

export function emailReportPdfFilename(report: EmailReport): string {
  const local = ((report.email ?? 'usuario').split('@')[0] ?? 'usuario').replace(/[^a-zA-Z0-9._-]/g, '_');
  return `email-report-${local}-${report.generated_at.slice(0, 10)}.pdf`;
}

/**
 * react-pdf no renderiza SVG como <Image>. Si el logo del partner vino en SVG
 * (data URI inlineado por el backend), lo rasterizamos acá con canvas — el
 * browser renderiza SVG nativamente y un data URI no contamina el canvas.
 * A 4× del tamaño final en el PDF (~92pt) queda nítido también impreso.
 */
async function rasterizeSvgDataUri(svgDataUri: string): Promise<string | null> {
  try {
    const img = new Image();
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('svg load failed'));
      img.src = svgDataUri;
    });
    // SVG sin width/height explícitos puede reportar 0 — fallback razonable.
    const w = img.naturalWidth || 360;
    const h = img.naturalHeight || 100;
    const scale = Math.min(4, 500 / w);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } catch {
    return null; // sin logo — el PDF sale igual
  }
}

export async function downloadEmailReportPdf(report: EmailReport): Promise<void> {
  const [{ pdf }, { EmailReportPdfDoc }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('./pdf/EmailReportPdfDoc'),
  ]);

  let printable = report;
  if (report.partner?.logo_data_uri?.startsWith('data:image/svg')) {
    const png = await rasterizeSvgDataUri(report.partner.logo_data_uri);
    printable = { ...report, partner: { ...report.partner, logo_data_uri: png } };
  }

  const element = createElement(EmailReportPdfDoc, { report: printable }) as ReactElement<DocumentProps>;
  const blob = await pdf(element).toBlob();

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = emailReportPdfFilename(report);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Diferido: en algunos browsers revocar sincrónicamente aborta la descarga.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
